// src/components/SubscriptionExpirySoonBanner.tsx
// Banner finito, no intrusivo, que aparece cuando la suscripción está por vencer
// (1 día antes o menos). Se auto-oculta a los 5 segundos y puede cerrarse.
//
// Requisito del user: "un dia antes o tres dias antes le recuerde con un banner
// finito que esta proximo a vencer, pero sea por unos segundos, nada intrusivo".
//
// Reglas:
//  - NO dispara logout
//  - NO bloquea la UI
//  - Si el user clickea "Pagar", abre el modal/suscripción (onPayClick)

import React, { useEffect, useState } from 'react';
import { Clock, X } from 'lucide-react';

interface SubscriptionInfo {
  effectiveStatus?: string;
  willExpireSoon?: boolean;
  daysUntilExpiry?: number | null;
  isExpired?: boolean;
}

interface Props {
  subscription?: SubscriptionInfo | null;
  onPayClick?: () => void;
  autoDismissMs?: number;
}

const SESSION_DISMISS_KEY = 'expirySoonBannerDismissedAt';

const SubscriptionExpirySoonBanner: React.FC<Props> = ({
  subscription,
  onPayClick,
  autoDismissMs = 5000,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!subscription) return;
    if (subscription.isExpired) return; // si ya venció, el modal se encarga
    if (!subscription.willExpireSoon) return;

    // Si el user ya lo cerró hace menos de 30 minutos, no volver a mostrarlo
    try {
      const dismissed = sessionStorage.getItem(SESSION_DISMISS_KEY);
      if (dismissed) {
        const ageMs = Date.now() - Number(dismissed);
        if (!Number.isNaN(ageMs) && ageMs < 30 * 60 * 1000) return;
      }
    } catch {
      // sessionStorage puede fallar en modo incógnito; ignoramos
    }

    setVisible(true);
    const t = window.setTimeout(() => setVisible(false), autoDismissMs);
    return () => window.clearTimeout(t);
  }, [subscription, autoDismissMs]);

  if (!visible || !subscription) return null;

  const days = typeof subscription.daysUntilExpiry === 'number' ? subscription.daysUntilExpiry : null;
  const label =
    days === 0
      ? 'Tu suscripción vence hoy'
      : days === 1
        ? 'Tu suscripción vence mañana'
        : 'Tu suscripción está por vencer';

  const handleClose = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  const handlePay = () => {
    handleClose();
    if (onPayClick) onPayClick();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none animate-[fadeIn_200ms_ease-out]"
    >
      <div className="pointer-events-auto mt-2 flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-900 text-sm px-4 py-1.5 rounded-full shadow-md">
        <Clock className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium whitespace-nowrap">{label}</span>
        <button
          onClick={handlePay}
          className="text-amber-900 underline underline-offset-2 font-semibold hover:text-amber-700"
        >
          Pagar
        </button>
        <button
          onClick={handleClose}
          aria-label="Cerrar"
          className="text-amber-700 hover:text-amber-900"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SubscriptionExpirySoonBanner;
